"use client"

import React, { useState, useRef } from "react"
import { cn } from "../../lib/utils"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Phone, Check, X } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

export interface PhoneNumberInputSimpleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size'> {
  value?: string
  onChange?: (value: string) => void
  label?: string
  error?: string
  helperText?: string
  required?: boolean
  showValidationIcon?: boolean
  inputClassName?: string
  labelClassName?: string
  errorClassName?: string
  size?: 'sm' | 'md' | 'lg'
}

// Basit telefon numarası doğrulama
function validatePhoneNumber(number: string): boolean {
  if (!number) return false
  const cleaned = number.replace(/\D/g, '')
  return cleaned.length >= 10 && cleaned.length <= 15
}

// Telefon numarası formatlama
function formatPhoneNumber(number: string): string {
  const cleaned = number.replace(/\D/g, '')
  
  // Türkiye formatı için basit formatlama
  if (cleaned.length <= 3) {
    return cleaned
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  } else {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`
  }
}

export const MoonUIPhoneNumberInputSimple = React.forwardRef<HTMLInputElement, PhoneNumberInputSimpleProps>(({
  value = '',
  onChange,
  label,
  error,
  helperText,
  required,
  showValidationIcon = true,
  className,
  inputClassName,
  labelClassName,
  errorClassName,
  disabled,
  placeholder = "(555) 123 45 67",
  size,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false)
  const [localValue, setLocalValue] = useState(value)
  const isValid = validatePhoneNumber(localValue)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const formattedValue = formatPhoneNumber(newValue)
    
    setLocalValue(formattedValue)
    onChange?.(formattedValue)
  }

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={props.id} className={labelClassName}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          <Phone className="h-4 w-4" />
        </div>
        
        <Input
          ref={ref}
          type="tel"
          value={localValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={cn(
            "pl-10 pr-10",
            error && "border-destructive",
            inputClassName
          )}
          disabled={disabled}
          {...props}
        />
        
        {/* Doğrulama İkonu */}
        {showValidationIcon && localValue && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <AnimatePresence mode="wait">
              {isValid ? (
                <motion.div
                  key="valid"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Check className="w-4 h-4 text-green-500" />
                </motion.div>
              ) : (
                <motion.div
                  key="invalid"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-4 h-4 text-destructive" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Helper Text veya Error */}
      {(error || helperText) && (
        <AnimatePresence mode="wait">
          {error ? (
            <motion.p
              key="error"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={cn("text-sm text-destructive", errorClassName)}
            >
              {error}
            </motion.p>
          ) : (
            <motion.p
              key="helper"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-sm text-muted-foreground"
            >
              {helperText}
            </motion.p>
          )}
        </AnimatePresence>
      )}
    </div>
  )
})

MoonUIPhoneNumberInputSimple.displayName = "MoonUIPhoneNumberInputSimple"